import React, { useMemo } from 'react';
import { Container, Row, Col, Image } from 'react-bootstrap';
import Text from '../shared/Text';
import styled, { css } from 'styled-components';
import ScreenWrapper from '../shared/ScreenWrapper';
import StoreButton from '../shared/StoreButton';
import Button from '../shared/Button';
import {
    model_s_line,
    updated_model_s_line,
    model_3_line,
    updated_model_3_line,
    model_x_line,
    updated_model_x_line,
    model_y_line,
    updated_model_y_line,
    cybertruck_line,
    updated_cybertruck_line,
    r1s_line,
    updated_r1s_line,
    r1t_line,
    updated_r1t_line,
    car_score,
    cybertruck,
    facts_tile,
    portal_to_beyond,
    range_rings,
    sweet_car_image,
    vehicle_group,
    zoomy_tesla,
    AppleIcon,
    AndroidIcon,
    gizmodo,
    tech_crunch,
    unece_logo,
    yahoo
} from '../../utility/icons';
import ProofStrip from '../shared/ProofStrip';
import styles from '../../utility/styles';

// margin trendline
// y = 25068x^-1.627
const updateMargin = width => {
    return 25068 * Math.pow(width, -1.627);
}

// image widths:

const LG = `${278 / 1241 * 100}%`
const LG_INFO = `${400 / 1241 * 100}%`

const MD_SMALL = `${310 / 1024 * 100}%`
const MD_LG = `${(1024 - 310) / 1024 * 100}%`

const SM_SMALL = `${310 / 744 * 100}%`
const SM_LG = `${(744 - 310) / 744 * 100}%`

const XS_SMALL = '100%'


const leftMargin = css`
    margin-left: -35%;
`

const leftMargingTablet = css`
    margin-left: -45%;
`

const rightMargin = css`
    margin-left: 35%;
`

const rightMarginTablet = css`
    margin-left: 45%;
`

const leftMarginPhone = css`
    margin-left: -150%;
`

const rightMarginPhone = css`
    margin-left: 150%;
`

const paddedContainer = css`
    padding: 0 120px;
    @media only screen and (max-width: 1024px) {
        padding: 0 64px;
    }
    @media only screen and (max-width: 744px) {
        padding: 0 48px;
    }
`

const paddedCol = css`
    padding: 0 20%;
    @media only screen and (max-width: 1024px) {
        padding: 0 64px;
    }
    @media only screen and (max-width: 744px) {
        padding: 0 48px;
    }
`

const noPaddingMobile = css`
    @media only screen and (max-width: 480px) {
        padding: 0;
    }
`

const StyledContainer = styled(Container)`
    margin: ${({ margin }) => margin || '0 0 160px 0'};
    align-self: center;
    width: 100%;
    overflow: ${({ overflow }) => overflow ? 'visible' : 'hidden'};
    @media only screen and (max-width: 1024px) {
        overflow: hidden;
        margin: ${({ laptopMargin }) => laptopMargin || '0 0 120px 0'};
    }
    @media only screen and (max-width: 744px) {
        margin: ${({ tabletMargin }) => tabletMargin || '0 0 96px 0'};
    }
    @media only screen and (max-width: 480px) {
        margin: ${({ phoneMargin }) => phoneMargin || '0 0 64px 0'};
    }
    &.padded {
        ${paddedContainer};
    }
    &.no-padding-mobile {
        ${noPaddingMobile};
    }
`

const HeaderImagesContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: ${({ isLeft }) => isLeft ? 'flex-start' : 'flex-end'};
    @media only screen and (max-width: 980px) {
        align-items: ${({ isLeft }) => isLeft ? 'flex-end' : 'flex-start'};
    }
`

const margins = [
    20, 10, 25, 5
]


const imageHeight = css`
    height: 54px;
`

const StyledImage = styled(Image)`
    height: ${({ $lead }) => ($lead ? '78px' : '48px')};
    opacity: ${({ $lead }) => ($lead ? 1 : 0.8)};
    margin-left: ${({ $step = 0, isLeft }) => (isLeft ? `${$step * 7}vw` : '0')};
    @media only screen and (max-width: 980px) {
        margin: ${({ isLeft, order }) => isLeft ? `0 ${margins[order]}% 0 0` : `0 0 0 ${margins[order]}%`};
        height: ${({ $lead }) => ($lead ? '58px' : '42px')};
    }
`

const ColumnImage = styled.div`
    height: 327px;
    background: center / contain url(${({ src }) => src}) no-repeat;
    @media only screen and (max-width: 480px) {
        background-size: ${({ mobile }) => mobile || 'cover'};
    }
`

const ThoughtBubble = styled.div`
    display: flex;
    padding: 16px;
    align-self: flex-start;
    gap: 10px;
    border-radius: 40px 40px 4px 40px;
    background: ${styles.colors.darkBackground};
    border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
    margin-bottom: 24px;
    @media only screen and (max-width: 480px) {
        align-self: center;
    }
`

const InfoRow = styled(Row)`
    margin: 0 0 40px 0;
`

const FactsCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin: 0 16px 80px 16px;
    flex: 1;
`

const FeaturedOnCol = styled(Col)`
    margin: 0 0 32px 0;
    display: flex;
    justify-content: center;
    align-items: center;
`

const StyledRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    &.shift-center {
        @media only screen and (max-width: 480px) {
            justify-content: center;
        }
    }
`

const StyledCol = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    &.padded {
        ${paddedCol};
    }
    &.shift-center {
        @media only screen and (max-width: 480px) {
            align-items: center;
        }
    }
`

const FunkyImagesContainer = styled.div`
    width: 100%;
    display: flex;
    margin-bottom: 120px;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 120px;
    @media only screen and (max-width: 1024px) {
        padding: 0 64px;
    }
    @media only screen and (max-width: 744px) {
        margin-bottom: 96px;
        padding: 0 48px;
    }
    @media only screen and (max-width: 480px) {
        margin-bottom: 64px;
        padding: 0 16px;
    }
`

const FunkyImage = styled.div`
    background-image: ${({ src, isFacts }) => (isFacts ? 'none' : `url(${src})`)};
    background-color: ${({ isFacts }) => (isFacts ? 'var(--tz-surface-raised, #1A202C)' : 'transparent')};
    background-size: cover;
    background-position: center;
    height: 400px;
    width: ${({ isFacts }) => isFacts ? `calc(${LG_INFO} - 6px)` : `calc(${LG} - 6px)`};
    border-radius: 16px;
    container-type: ${({ isFacts }) => (isFacts ? 'inline-size' : 'normal')};
    padding: 48px;
    display: flex;
    flex-direction: column;
    justify-content: ${({ isFacts }) => (isFacts ? 'flex-start' : 'space-between')};
    @media only screen and (max-width: 1024px) {
        width: ${({ isLarge }) => isLarge ? `calc(${MD_LG} - 4px)` : `calc(${MD_SMALL} - 4px)`};
    }
    @media only screen and (max-width: 744px) {
        width: ${({ isLarge }) => isLarge ? `calc(${SM_LG} - 4px)` : `calc(${SM_SMALL} - 4px)`};
        padding: 32px 28px;
    }
    @media only screen and (max-width: 480px) {
        width: ${XS_SMALL};
        padding: 28px 24px;
    }
`


const LiveDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${styles.colors.success};
    flex-shrink: 0;
`

const LiveEyebrow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    font-family: ${styles.fonts.geistMono};
    font-size: 11px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${styles.colors.mediumGray};
    & b {
        color: ${styles.colors.textPrimary};
        font-weight: 600;
    }
`

const OdometerValue = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-variant-numeric: tabular-nums;
    /* sized against the tile (container query), not the viewport: 13 mono glyphs
       at ~0.65em advance fit within ~88% of the tile's content width */
    font-size: 32px;
    font-size: clamp(22px, 10.5cqw, 52px);
    line-height: 1.05;
    letter-spacing: -0.01em;
    font-weight: 500;
    white-space: nowrap;
    color: ${styles.colors.success};
    /* commas get a full mono cell; pull them back toward the preceding digit */
    & .sep {
        margin-left: -0.16em;
        margin-right: -0.04em;
    }
`

const StatGrid = styled.div`
    border-top: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
    padding-top: 24px;
    margin-top: auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
`

const StatValue = styled.div`
    font-family: ${styles.fonts.geistMono};
    font-variant-numeric: tabular-nums;
    font-size: 22px;
    line-height: 28px;
    font-weight: 500;
    color: ${styles.colors.textPrimary};
`

const StoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 32px;
    border-radius: 12px;
    background-color: ${styles.colors.darkBackground};
    border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
    @media only screen and (max-width: 480px) {
        margin: 8px 0;
    }
`

const Logo = styled.div`
    margin-bottom: 16px;
`

const FeaturedImage = styled.div`
    background: center / ${({ size }) => size || 'contain'} url(${({ src }) => src}) no-repeat;
    width: 100%;
    height: 35px;
    opacity: ${({ theme }) => (theme.name === 'light' ? 0.75 : 0.55)};
    filter: ${({ theme }) => (theme.name === 'light' ? 'invert(0.82)' : 'none')};
    @media only screen and (max-width: 744px) {
        background-size: contain;
    }
`

const InfoHeading = styled(Text.Heading)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoBody = styled(Text.Body)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoCaption = styled(Text.Caption)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoHuge = styled(Text.Huge)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoButton = styled(Button)`
    text-decoration: none !important;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 9px 20px;
    background-color: ${styles.colors.transparent};
    border: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.14));
    border-radius: 10px;
    font-family: ${styles.fonts.geist};
    font-weight: 500;
    color: ${styles.colors.textPrimary};
    align-self: flex-start;
    &:active, &:hover, &:visited {
        color: ${styles.colors.textPrimary};
    }
    @media only screen and (max-width: 480px) {
        align-self: center;
    }
`

const LinkButton = styled(Button)`
    text-decoration: none !important;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 9px 20px;
    background-color: ${({ variant }) => variant === 'secondary' ? styles.colors.transparent : styles.colors.textPrimary};
    border: 1px solid ${({ variant }) => variant === 'secondary' ? 'var(--tz-hairline-strong, rgba(255, 255, 255, 0.14))' : styles.colors.textPrimary};
    border-radius: 10px;
    font-family: ${styles.fonts.geist};
    font-weight: 500;
    color: ${({ variant }) => variant === 'secondary' ? styles.colors.textPrimary : styles.colors.black};
    align-self: flex-start;
    &:active, &:hover, &:visited {
        color: ${({ variant }) => variant === 'secondary' ? styles.colors.textPrimary : styles.colors.black};
    }
`

// Live fleet miles readout — server-provided figures, slot-text rolls the digits.
function LiveMilesValue({ odometer }) {
    const ref = React.useRef(null)
    React.useEffect(() => {
        const el = ref.current
        if (!el) return
        const compute = () => odometer.baseline_miles + (Math.max(0, Date.now() - odometer.baseline_at_ms) / 1000) * odometer.miles_per_second
        const fmt = v => Math.floor(v).toLocaleString('en-US')
        // Frame-rate text updates so every integer is rendered as it passes.
        let raf
        let last = ''
        const step = () => {
            const next = fmt(compute())
            if (next !== last) {
                el.innerHTML = next.replace(/,/g, '<span class="sep">,</span>')
                last = next
            }
            raf = window.requestAnimationFrame(step)
        }
        step()
        return () => window.cancelAnimationFrame(raf)
    }, [])
    return <OdometerValue ref={ref} />
}

export default function Home({ ratings, fleet_odometer }) {
    // Only renders once server figures are present.
    const hasOdometer = !!(fleet_odometer?.baseline_miles && fleet_odometer?.baseline_at_ms)
    const [margin, setMargin] = React.useState(0);
    React.useEffect(() => {
        const fixMargin = () => {
            const m = updateMargin(window.innerWidth);
            // setMargin(m);
        }

        window.addEventListener('resize', fixMargin);

        fixMargin();

        return () => {
            window.removeEventListener('resize', fixMargin);
        }
    }, [])
    return (
        <ScreenWrapper style={{ overflowX: 'hidden' }}>
            <HeaderImagesContainer count={3} isLeft style={{ marginTop: '72px' }}>
                <StyledImage src={updated_r1t_line} order={0} $delay={0} $step={0} isLeft />
                <StyledImage src={updated_model_3_line} order={1} $delay={1} $step={1} isLeft />
                <StyledImage src={updated_model_s_line} order={2} $delay={2} $step={2} $lead isLeft />
            </HeaderImagesContainer>
            <StyledCol className='padded'>
                <Text.Huge allowWidows style={{ marginBottom: '12px', marginTop: '40px', maxWidth: '900px' }} type='mega' variant='bold' color={styles.colors.textPrimary} tablet='h1'>The #1 rated Tesla and Rivian app in the world.</Text.Huge>
                <Text.Heading style={{ marginBottom: '20px' }} type='h4' color={styles.colors.lightGray}>Now supporting FSD/Autopilot analysis.</Text.Heading>
                <Text.Heading style={{ marginBottom: '28px', maxWidth: '640px' }} type='h5' color={styles.colors.lightGray} tablet='body'>Download today and join the largest platform of Tesla and Rivian owners who are saving money and getting the most from their EV.</Text.Heading>
                <StyledRow style={{ marginBottom: '72px', justifyContent: 'flex-start' }}>
                    <StoreButton type='apple' style={{ width: '150px', maxWidth: '150px', height: '50px', maxHeight: '50px' }} />
                    <StoreButton type='android' style={{ width: '150px', maxWidth: '150px', height: '50px', maxHeight: '50px' }} />
                </StyledRow>
            </StyledCol>
            <HeaderImagesContainer count={4} style={{ marginBottom: '80px' }}>
                <StyledImage src={updated_model_y_line} order={0} $delay={3} />
                <StyledImage src={updated_cybertruck_line} order={1} $delay={4} />
                <StyledImage src={updated_r1s_line} order={2} $delay={5} />
                <StyledImage src={updated_model_x_line} order={3} $delay={6} $lead />
            </HeaderImagesContainer>
            <ProofStrip {...{ ratings }} />
            <StyledContainer className='padded' fluid='lg'>
                <Row>
                    <Col xs={12} sm={6} className='order-2 order-sm-1 d-flex flex-column'>
                        <InfoHeading type='h1' tablet='h2' variant='bold' color={styles.colors.dimHighlight}>We get EVs.</InfoHeading>
                        <InfoHeading tablet='body' style={{ marginBottom: '16px' }} type='h5' color={styles.colors.dimHighlight}>From saving you money to daily commuting, road tripping, and getting the most from your battery, it's no wonder TezLab is the most popular EV app in the world. We put the charge in your battery!</InfoHeading>
                    </Col>
                    <Col xs={12} sm={6} className='order-1 order-sm-2'>
                        <ColumnImage src={vehicle_group} mobile='contain' />
                    </Col>
                </Row>
            </StyledContainer>
            <StyledContainer className='padded' margin='0 0 80px 0' laptopMargin='0 0 64px 0' tabletMargin='0 0 48px 0' phoneMargin='0 0 40px 0' fluid='lg'>
                <Row>
                    <Col>
                        <Text.Heading type='h1' tablet='h2' variant='bold' style={{ textAlign: 'center' }} color={styles.colors.dimHighlight}>You'll love TezLab, here's why:</Text.Heading>
                    </Col>
                </Row>
            </StyledContainer>
            <StyledContainer className='padded no-padding-mobile' fluid='lg'>
                <InfoRow>
                    <Col xs={12} sm={6}>
                        <ColumnImage src={car_score} />
                    </Col>
                    <Col xs={12} sm={6} className='d-flex flex-column'>
                        <ThoughtBubble>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M4.68482 22.0131C4.68512 22.1786 4.81935 22.3126 4.98482 22.3126H20.0612H20.897H24.3641C24.5298 22.3126 24.6641 22.1783 24.6641 22.0126V10.6004C24.6641 10.4348 24.5298 10.3004 24.3641 10.3004H20.6641H20.2249H4.96461C4.79872 10.3004 4.66432 10.4351 4.66461 10.601L4.68482 22.0131ZM2.95457 24.3065H18.4523H20.4159H26.3641C26.5298 24.3065 26.6641 24.1722 26.6641 24.0065V20.3065H28.3641C28.5298 20.3065 28.6641 20.1722 28.6641 20.0065V12.6065C28.6641 12.4408 28.5298 12.3065 28.3641 12.3065H26.6641V8.60632C26.6641 8.44063 26.5298 8.30652 26.3641 8.30652H22.7151H20.707H2.96406C2.79838 8.30652 2.66406 8.44083 2.66406 8.60652V24.0065C2.66406 24.1722 2.78888 24.3065 2.95457 24.3065ZM6.19742 11.6399C6.08696 11.6399 5.99742 11.7294 5.99742 11.8399V20.7732C5.99742 20.8836 6.08696 20.9732 6.19741 20.9732H19.1307C19.2412 20.9732 19.3307 20.8836 19.3307 20.7732V11.8399C19.3307 11.7294 19.2412 11.6399 19.1307 11.6399H6.19742Z" fill="#10ABFF" />
                            </svg>
                        </ThoughtBubble>
                        <InfoHeading style={{ marginBottom: '16px' }} type='h5' color={styles.colors.dimHighlight}>Battery and Car Health</InfoHeading>
                        <InfoBody color={styles.colors.dimHighlight}>Experience real-time monitoring of your Tesla or Rivian's battery health with our advanced analytics. Get personalized insights about degradation, charging patterns, and maximize your battery's longevity with our smart recommendations.</InfoBody>
                    </Col>
                </InfoRow>
                <InfoRow>
                    <Col xs={12} sm={6}>
                        <ColumnImage src={range_rings} />
                    </Col>
                    <Col xs={12} sm={6} className='d-flex flex-column'>
                        <ThoughtBubble>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                                <path d="M6 16.3954H9.90008C10.0118 16.3954 10.11 16.3213 10.1405 16.2138L12.6144 7.51548C12.6824 7.2763 13.0199 7.2723 13.0936 7.5098L18.5709 25.1679C18.6427 25.3995 18.9693 25.4034 19.0466 25.1735L21.9427 16.5657C21.9769 16.4639 22.0723 16.3954 22.1797 16.3954H26" stroke="#10ABFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </ThoughtBubble>
                        <InfoHeading color={styles.colors.dimHighlight} style={{ marginBottom: '16px' }} type='h5'>Realtime Driving and Charging Awareness</InfoHeading>
                        <InfoBody color={styles.colors.dimHighlight}>Stay ahead with live updates on your driving efficiency and charging status. Make informed decisions about your charging stops with our comprehensive charging map and real-time availability updates.</InfoBody>
                    </Col>
                </InfoRow>
                <InfoRow>
                    <Col xs={12} sm={6}>
                        <ColumnImage src={portal_to_beyond} mobile='contain' />
                    </Col>
                    <Col xs={12} sm={6} className='d-flex flex-column'>
                        <ThoughtBubble>
                            <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <mask id="mask0_3608_44078" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="2" y="2" width="28" height="28">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M20.9974 2.97321C20.4451 2.97321 19.9974 3.42092 19.9974 3.97321L25.8712 4.39452L19.7916 9.71062L14.58 4.61156C14.0624 4.10514 13.2352 4.10442 12.7167 4.60993L3.06661 14.0185C2.53935 14.5326 2.52866 15.3767 3.04272 15.904C3.55678 16.4313 4.40093 16.442 4.92819 15.9279L13.6459 7.42837L18.8083 12.4793C19.3059 12.9662 20.0944 12.9882 20.6184 12.53L26.6641 7.24361V11.3065C26.6641 11.8588 27.1118 12.3065 27.6641 12.3065H28.3307C28.883 12.3065 29.3307 11.8588 29.3307 11.3065V4.64266L29.3307 4.63987V4.31302C29.3308 4.30835 29.3308 4.30368 29.3307 4.299V3.9732C29.3307 3.62803 29.1558 3.3237 28.8898 3.14399C28.7302 3.03617 28.5378 2.97321 28.3307 2.97321H27.9969H27.6641H20.9974ZM29.2886 4.63964L29.3307 4.64266C29.3294 5.12962 28.98 5.53474 28.5182 5.62232L28.8751 5.31028C29.0853 5.12644 29.2239 4.89115 29.2886 4.63964ZM10.6641 14.9732H14.6641V29.6399H10.6641V14.9732ZM3.9974 20.3065H7.9974V29.6399H3.9974V20.3065ZM21.3307 18.9732H17.3307V29.6399H21.3307V18.9732ZM23.9974 16.3065H27.9974V29.6399H23.9974V16.3065Z" fill="white" />
                                </mask>
                                <g mask="url(#mask0_3608_44078)">
                                    <rect y="0.306519" width="32" height="32" fill="#10ABFF" />
                                </g>
                            </svg>
                        </ThoughtBubble>
                        <InfoHeading color={styles.colors.dimHighlight} style={{ marginBottom: '16px' }} type='h5'>Save Money With Trends</InfoHeading>
                        <InfoBody color={styles.colors.dimHighlight}>Dive deep into your vehicle's performance metrics with our industry-leading analytics to help you save money. Charge at cost effective times. Compare your stats with the TezLab community and unlock the full potential of your EV experience.</InfoBody>
                    </Col>
                </InfoRow>
            </StyledContainer>
            <StyledContainer className='padded' fluid='lg'>
                <Row>
                    <Col xs={12} sm={6} lg={4} className='d-flex'>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path d="M16.8386 4.80652C8.75172 4.80652 2.17188 11.3864 2.17188 19.4732C2.17188 22.6185 3.15443 25.6178 5.01498 28.1486C5.24558 28.4622 5.61081 28.647 6.00039 28.647H27.6781C28.0663 28.647 28.4329 28.4622 28.6635 28.1486C30.5226 25.6191 31.5052 22.6185 31.5052 19.4732C31.5052 11.3864 24.9254 4.80652 16.8385 4.80652H16.8386ZM19.4869 26.2034H14.1918C14.6559 25.2022 15.6642 24.5018 16.8387 24.5018C18.0133 24.5018 19.0215 25.2022 19.4871 26.2034H19.4869ZM27.041 26.2034H22.0624C21.7558 24.8928 20.9709 23.7671 19.8996 23.0194L25.1704 15.103C25.5443 14.5416 25.3925 13.7825 24.831 13.4086C24.2681 13.0348 23.5104 13.1866 23.1366 13.7481L17.5676 22.1114C17.327 22.0799 17.0864 22.057 16.8386 22.057C14.2991 22.057 12.1709 23.8316 11.6165 26.2036H6.63792C5.31305 24.2041 4.61697 21.8909 4.61697 19.4734C4.61697 12.7345 10.0997 7.25169 16.8387 7.25169C23.5777 7.25169 29.0605 12.7345 29.0605 19.4734C29.0605 21.8912 28.3644 24.2042 27.0409 26.2036L27.041 26.2034Z" fill="#10ABFF" />
                            </svg>
                            <div>
                                <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>The Dash</Text.Heading>
                                <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>New to Tesla or a veteren, the EV lifestyle is made easy with a customizable dashboard.</Text.Body>
                            </div>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4} className='d-flex'>
                        <FactsCol>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M1 8C0.447715 8 0 8.44772 0 9V23.2222C0 23.7745 0.447715 24.2222 1 24.2222H31C31.5523 24.2222 32 23.7745 32 23.2222V9C32 8.44772 31.5523 8 31 8H1ZM5.33334 12.5557V19.6668H10.2698V18.3454H6.92408V12.5557H5.33334ZM11.2164 12.5557V19.6668H12.8072V12.5557H11.2164ZM16.6034 19.6668L13.35 12.5557H15.0537L17.291 17.6595L19.5283 12.5557H21.16L17.9067 19.6668H16.6034ZM21.7097 12.5557V19.6668H26.6667V18.4463H23.2286V16.671H26.4409V15.4405H23.2286V13.7762H26.6667V12.5557H21.7097Z" fill="#10ABFF" />
                            </svg>
                            <div>
                                <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Real Time Insights</Text.Heading>
                                <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>We make range easy to understand by providing live real world calculations.</Text.Body>
                            </div>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4} className='d-flex'>
                        <FactsCol>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M17.1311 6.56661L18.406 6.6543C18.5294 6.66238 18.5933 6.77319 18.5259 6.8612L15.4145 10.9185C15.3831 10.9594 15.3377 10.9807 15.2911 10.9864C15.1919 10.9986 15.0869 10.938 15.104 10.8374L15.6991 7.23716C15.7114 7.16094 15.6467 7.09418 15.5537 7.08739L14.2789 7.00032C14.1562 6.99153 14.0922 6.88072 14.1589 6.7928L17.2704 2.73607C17.3017 2.69518 17.3471 2.6739 17.3937 2.66819C17.4929 2.65601 17.5979 2.71595 17.5815 2.81652L16.9857 6.41683C16.9733 6.49306 17.0381 6.56044 17.1311 6.56661ZM6.0026 20.1696C7.84355 20.1696 9.33594 18.6773 9.33594 16.8363C9.33594 14.9954 7.84355 13.503 6.0026 13.503C4.16165 13.503 2.66927 14.9954 2.66927 16.8363C2.66927 18.6773 4.16165 20.1696 6.0026 20.1696ZM5.59412 21.503C3.24239 21.503 1.33594 23.4094 1.33594 25.7612C1.33594 25.7661 1.33937 25.7704 1.34421 25.7715L3.03161 26.1572C4.73661 26.5469 6.49912 26.5968 8.21886 26.307C8.49846 25.1317 9.03818 24.0575 9.7733 23.1479C8.99416 22.1469 7.77782 21.503 6.41109 21.503H5.59412ZM23.7863 26.307C23.5025 25.1138 22.9506 24.0249 22.1983 23.1066C22.9536 22.131 24.1362 21.503 25.4654 21.503H26.5398C28.8204 21.503 30.6693 23.3518 30.6693 25.6325C30.6693 25.7127 30.6138 25.7823 30.5355 25.8002L28.9736 26.1572C27.2686 26.5469 25.5061 26.5968 23.7863 26.307ZM11.5816 25.1831C12.5412 23.7672 14.1652 22.8363 16.0026 22.8363C18.0094 22.8363 19.7617 23.9469 20.6718 25.5869L20.686 25.6127L20.6911 25.622C21.1022 26.3762 21.3359 27.2406 21.3359 28.1592C21.3359 28.1654 21.3318 28.1707 21.3259 28.1722L19.2364 28.6945C18.971 28.7609 18.7041 28.8189 18.436 28.8687C16.5594 29.217 14.6266 29.159 12.7688 28.6945L10.6794 28.1722C10.6734 28.1707 10.6693 28.1654 10.6693 28.1592C10.6693 27.2368 10.9049 26.3691 11.3192 25.6127L11.3334 25.5871L11.3334 25.5869C11.4103 25.4484 11.4931 25.3137 11.5816 25.1831ZM29.3359 16.8363C29.3359 18.6773 27.8436 20.1696 26.0026 20.1696C24.1617 20.1696 22.6693 18.6773 22.6693 16.8363C22.6693 14.9954 24.1617 13.503 26.0026 13.503C27.8436 13.503 29.3359 14.9954 29.3359 16.8363ZM20.0026 17.503C20.0026 19.7121 18.2117 21.503 16.0026 21.503C13.7935 21.503 12.0026 19.7121 12.0026 17.503C12.0026 15.2938 13.7935 13.503 16.0026 13.503C18.2117 13.503 20.0026 15.2938 20.0026 17.503ZM9.9249 8.08586L9.19561 8.9074C9.1426 8.9674 9.06431 8.9767 9.02088 8.929L6.97683 6.66647C6.91956 6.60349 6.82498 6.64393 6.78068 6.71766C6.7599 6.75225 6.7499 6.7941 6.76014 6.83654L7.77743 11.0448C7.79988 11.1355 7.90616 11.1492 7.97658 11.0703L8.70542 10.2485C8.75888 10.1888 8.83673 10.1792 8.88016 10.2269L10.9245 12.4889C10.9815 12.5524 11.0765 12.5122 11.1208 12.4385C11.1416 12.4039 11.1516 12.3621 11.1413 12.3196L10.1236 8.11108C10.1015 8.01984 9.99522 8.00615 9.9249 8.08586ZM23.8971 9.95208L24.742 10.6514C24.824 10.7188 24.8139 10.8255 24.7236 10.8508L20.5575 12.0172C20.5155 12.029 20.4734 12.0205 20.4382 12.0009C20.3631 11.9593 20.3197 11.8658 20.3812 11.8067L22.5692 9.68389C22.6153 9.63881 22.6031 9.56138 22.5417 9.5101L21.6965 8.81124C21.6153 8.7437 21.6253 8.63704 21.7151 8.61139L25.8809 7.44541C25.9229 7.43366 25.965 7.44216 26.0003 7.4617C26.0754 7.50333 26.119 7.59637 26.0581 7.65583L23.8695 9.77829C23.8234 9.82337 23.8354 9.90125 23.8971 9.95208Z" fill="#10ABFF" />
                            </svg>
                            <div>
                                <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Our Community</Text.Heading>
                                <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Create public and private groups and share trips, zaps, and charging locations with friends.</Text.Body>
                            </div>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4} className='d-flex'>
                        <FactsCol>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M31.7667 16.028C31.6913 15.2992 31.269 14.6508 30.6332 14.2864L28.3815 12.9997H29.9849C30.3618 13.0047 30.7313 12.8991 31.0454 12.6906C31.3093 12.5398 31.4827 12.2658 31.5028 11.9643C31.5028 10.7555 29.5727 10.484 28.6202 10.484C28.1829 10.484 28.2734 11.2555 28.3764 11.7707H27.9567C27.4844 10.1416 25.8214 7.62253 25.1425 6.63456C24.9574 6.36527 24.6702 6.19095 24.3461 6.14981C23.0634 5.98703 19.5277 5.58361 15.9916 5.58386C12.3902 5.58412 8.78913 6.00348 7.56577 6.16067C7.28293 6.19701 7.02417 6.33651 6.84633 6.55943C6.21815 7.34688 4.70379 9.40918 4.01918 11.7707H3.602C3.70504 11.2681 3.79551 10.484 3.35822 10.484C2.43841 10.484 0.475636 10.7554 0.475636 11.9643H0.473123C0.493228 12.2658 0.666634 12.5398 0.930515 12.6906C1.24466 12.8991 1.61409 13.0047 1.99103 12.9997H3.61199L1.36022 14.2864C0.917906 14.5402 0.573578 14.9373 0.382628 15.4123C0.299692 15.6159 0.246917 15.8345 0.229327 16.0556C-0.0546593 18.7899 -0.117484 25.1283 0.287129 26.5881V26.5856C0.367549 26.9048 0.412784 27.2315 0.42284 27.5582C0.42284 27.7216 0.55352 27.8547 0.716876 27.8547H4.53182C4.69518 27.8547 4.82837 27.7216 4.82837 27.5582V26.5408C4.82837 26.3976 4.94398 26.282 5.08722 26.282C5.08722 26.282 8.53503 26.5881 15.9916 26.5881C23.4481 26.5881 26.8959 26.282 26.8959 26.282C26.9638 26.282 27.0291 26.3096 27.0769 26.3574C27.1271 26.4051 27.1523 26.4705 27.1523 26.5408V27.5582C27.1523 27.7216 27.2855 27.8548 27.4488 27.8548H31.309C31.4724 27.8548 31.6055 27.7216 31.6055 27.5582C31.6093 27.3897 31.6246 27.1648 31.6492 26.9411C31.6741 26.7144 31.7086 26.489 31.7503 26.3245C32.1223 24.877 32.0506 18.7623 31.7667 16.028ZM7.12007 7.52097C7.35129 7.24704 7.68553 7.08117 8.04491 7.05855C10.6787 6.69666 13.3326 6.53164 15.9917 6.5417C18.6179 6.52913 21.2442 6.69415 23.8478 7.05855C24.2047 7.08117 24.5389 7.24704 24.7727 7.52097C25.7025 8.80268 26.5369 10.9961 26.6249 12.5744C26.6249 12.5744 26.1889 13.0467 15.9804 13.0467C5.77196 13.0467 5.33601 12.5744 5.33601 12.5744C5.39884 11.0062 6.22543 8.81271 7.12007 7.52097ZM26.0179 25.2959C25.6755 25.3242 23.4319 25.4822 15.9936 25.4822C8.55752 25.4822 6.32361 25.3243 5.98213 25.296C5.9539 25.2936 5.93315 25.2701 5.93315 25.2417C5.93315 24.5741 6.52193 24.0409 7.18838 24.0805C8.56198 24.1622 11.2324 24.2642 16 24.2642C20.7676 24.2642 23.4381 24.1622 24.8117 24.0805C25.4781 24.0409 26.0669 24.5741 26.0669 25.2417C26.0669 25.27 26.0461 25.2936 26.0179 25.2959ZM26.5666 18.0402C25.5847 17.763 25.0191 17.213 24.9638 16.983C24.9283 16.8354 25.7892 16.4682 26.5209 16.156L26.521 16.156L26.521 16.156L26.521 16.156C26.9296 15.9817 27.2979 15.8246 27.4474 15.7325C27.6685 15.5963 28.0137 15.3236 28.3219 15.0803L28.3219 15.0802L28.322 15.0802L28.322 15.0802L28.322 15.0802C28.5952 14.8644 28.8392 14.6717 28.9419 14.6174C29.1603 14.5018 29.6639 14.6754 30.0321 15.0944C30.0321 15.0944 30.4497 15.8796 30.5241 16.2031C30.6258 16.6331 30.7027 17.1819 30.5833 17.5959C30.4447 18.0767 26.9748 18.1554 26.5666 18.0402ZM7.042 16.983C6.98671 17.213 6.42111 17.763 5.4392 18.0402C5.03097 18.1554 1.56112 18.0767 1.42244 17.5959C1.30304 17.1819 1.38 16.6331 1.48169 16.2031C1.55607 15.8796 1.97371 15.0944 1.97371 15.0944C2.34192 14.6754 2.84545 14.5018 3.0639 14.6174C3.16655 14.6717 3.41059 14.8644 3.68378 15.0802L3.68378 15.0802L3.68379 15.0802C3.99198 15.3236 4.33724 15.5963 4.5584 15.7325C4.70789 15.8246 5.0762 15.9817 5.4848 16.156C6.21655 16.4681 7.07748 16.8354 7.042 16.983ZM30.8622 21.0091C30.4149 21.0743 28.5733 21.3343 27.5124 21.351C27.4678 21.3517 27.4455 21.4061 27.4767 21.438C27.5466 21.5094 27.6074 21.5733 27.6614 21.6299C27.937 21.9192 28.0352 22.0222 28.2547 22.002C29.1434 21.9198 30.4297 21.7752 30.7031 21.7396C30.7031 21.7396 30.9701 23.2311 30.25 24.4429C30.1156 24.6691 30.354 24.965 30.5992 24.4429C31.2025 23.1579 31.0638 21.7718 30.9755 21.0945C30.9683 21.0391 30.9175 21.001 30.8622 21.0091ZM4.50093 21.351C3.44007 21.3343 1.59845 21.0743 1.15113 21.0091C1.09586 21.001 1.04504 21.0391 1.03782 21.0945C0.949541 21.7718 0.81082 23.1579 1.41415 24.4429C1.65933 24.965 1.89779 24.6691 1.76335 24.4429C1.04327 23.2311 1.31022 21.7396 1.31022 21.7396C1.58363 21.7752 2.86994 21.9198 3.75862 22.002C3.97818 22.0222 4.07633 21.9192 4.35197 21.6299C4.40597 21.5733 4.46678 21.5094 4.53665 21.438C4.56782 21.4061 4.54551 21.3517 4.50093 21.351Z" fill="#10ABFF" />
                            </svg>
                            <div>
                                <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Fast Car Controls</Text.Heading>
                                <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Easy access to all controls with customizable quick controls available on your dashboard.</Text.Body>
                            </div>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4} className='d-flex'>
                        <FactsCol>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M22.2723 16.2925L18.4702 15.4037C18.193 15.3397 18.0229 15.0655 18.0881 14.7936L21.21 1.95715C21.2962 1.59842 21.0026 1.33337 20.6997 1.33337C20.5576 1.33337 20.4132 1.3905 20.3037 1.5253L9.44712 14.8987C9.21415 15.1889 9.36558 15.6207 9.73135 15.7076L13.5335 16.5941C13.8107 16.6604 13.9808 16.9323 13.9156 17.2042L10.796 30.0406C10.7075 30.3994 11.0011 30.6667 11.3039 30.6667C11.446 30.6667 11.5905 30.6096 11.7 30.4748L22.5566 17.0991C22.7919 16.8089 22.6404 16.377 22.2723 16.2925Z" fill="#10ABFF" />
                            </svg>
                            <div>
                                <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Best in Class Stats</Text.Heading>
                                <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Review usage reports, charges, charge locations, and drives. Tag your drives to keep them organized.</Text.Body>
                            </div>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4} className='d-flex'>
                        <FactsCol>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M28.5209 12.8095C29.7058 12.8095 30.6693 13.7732 30.6693 14.9583V17.0444C30.6693 18.229 29.7056 19.1925 28.5209 19.1925H26.9466L26.0011 21.488L27.1064 22.5916C27.5135 22.9953 27.7392 23.5401 27.7392 24.115C27.7392 24.6897 27.5124 25.2332 27.1086 25.6336L25.6352 27.1107C24.8132 27.9315 23.4182 27.9311 22.5961 27.1092L21.4905 25.9998L19.1954 26.9415V28.5173C19.1954 29.704 18.2302 30.6667 17.0432 30.6667H14.9588C13.7736 30.6667 12.8118 29.7033 12.8118 28.5173V26.946L10.5135 25.9999L9.40428 27.1101C8.58491 27.9249 7.20376 27.9283 6.3697 27.1136L4.8936 25.6351C4.48786 25.2328 4.26148 24.689 4.26148 24.115C4.26148 23.5402 4.487 22.9959 4.89241 22.5927L6.00055 21.4853L5.05812 19.1925H3.48233C2.29762 19.1925 1.33594 18.2289 1.33594 17.0444V14.9583C1.33594 13.7733 2.29745 12.8095 3.48233 12.8095H5.05685L6.00134 10.5167L4.89358 9.4085C4.48729 9.00561 4.26148 8.4615 4.26148 7.88835C4.26148 7.31417 4.48706 6.77113 4.89239 6.36809L6.37027 4.89106C7.19162 4.07321 8.5804 4.07252 9.40505 4.89139L10.5144 6.00176L12.8118 5.05661V3.48283C12.8118 2.29731 13.7738 1.33337 14.9588 1.33337H17.0432C18.23 1.33337 19.1954 2.29654 19.1954 3.48283V5.05861L21.4886 6.00285L22.6012 4.88651C23.4252 4.07272 24.8084 4.07237 25.6341 4.88894L27.108 6.36778C27.5139 6.77142 27.7392 7.31458 27.7392 7.88835C27.7392 8.46246 27.5127 9.00678 27.1081 9.40685L26.0026 10.5133L26.946 12.8095H28.5209ZM28.5209 14.6054H26.7044C26.0837 14.6054 25.4973 14.143 25.3405 13.6174L24.3138 11.1142C24.0266 10.5884 24.1325 9.84171 24.5568 9.41947L25.8414 8.13371C25.9082 8.0677 25.9433 7.98341 25.9433 7.88835C25.9433 7.79205 25.9081 7.7074 25.8388 7.6384L24.3667 6.16134C24.2453 6.04127 23.9876 6.04133 23.8681 6.1593L22.5825 7.44926C22.1425 7.8851 21.4739 7.99589 20.9248 7.70944L18.4216 6.67704C17.8418 6.50564 17.3995 5.90212 17.3995 5.29976V3.48283C17.3995 3.28932 17.2391 3.12929 17.0432 3.12929H14.9588C14.7664 3.12929 14.6077 3.28837 14.6077 3.48283V5.29976C14.6077 5.91669 14.1399 6.50723 13.6193 6.66208L11.12 7.68832C10.6012 7.97451 9.84322 7.86647 9.41997 7.44726L8.13709 6.16326C8.01585 6.04286 7.75864 6.04299 7.63861 6.16251L6.1603 7.63996C6.09253 7.70734 6.05737 7.79198 6.05737 7.88835C6.05737 7.98349 6.09255 8.06825 6.1609 8.13604L7.44194 9.41755C7.88123 9.85279 7.96726 10.5986 7.70948 11.0738L6.67631 13.5823C6.5029 14.1539 5.90016 14.6054 5.30144 14.6054H3.48231C3.29024 14.6054 3.13184 14.7642 3.13184 14.9583V17.0444C3.13184 17.2378 3.29026 17.3966 3.48231 17.3966H5.30144C5.91561 17.3966 6.50324 17.8606 6.66337 18.3828L7.68858 20.883C7.97996 21.4139 7.87252 22.1571 7.44468 22.5811L6.1603 23.8646C6.09291 23.9317 6.05737 24.0174 6.05737 24.115C6.05737 24.2103 6.0926 24.2949 6.1613 24.3631L7.63255 25.8368C7.75744 25.9588 8.01577 25.9581 8.13587 25.8387L9.42029 24.5532C9.85828 24.1152 10.53 24.0061 11.0763 24.2932L13.5751 25.3236C14.1531 25.4925 14.6077 26.099 14.6077 26.701V28.5173C14.6077 28.7121 14.7661 28.8708 14.9588 28.8708H17.0432C17.2394 28.8708 17.3995 28.7111 17.3995 28.5173V26.701C17.3995 26.0814 17.8575 25.4935 18.3821 25.3383L20.8801 24.3158C21.3976 24.0281 22.1633 24.1343 22.5853 24.5543L23.867 25.8403C23.9869 25.9602 24.2456 25.9602 24.3649 25.8411L25.8406 24.3618C25.908 24.295 25.9433 24.2104 25.9433 24.115C25.9433 24.0177 25.9079 23.9323 25.8397 23.8647L24.5543 22.5814C24.1192 22.1443 24.0381 21.3994 24.295 20.9256L25.3267 18.4221C25.4986 17.8435 26.1012 17.3966 26.7044 17.3966H28.5209C28.7138 17.3966 28.8733 17.237 28.8733 17.0444V14.9583C28.8733 14.765 28.7138 14.6054 28.5209 14.6054ZM16.1026 12.0578C16.0407 12.0221 15.9645 12.0221 15.9026 12.0578L12.6385 13.9423C12.5766 13.978 12.5385 14.0441 12.5385 14.1155V17.8846C12.5385 17.956 12.5766 18.0221 12.6385 18.0578L15.9026 19.9423C15.9645 19.978 16.0407 19.978 16.1026 19.9423L19.3667 18.0578C19.4286 18.0221 19.4667 17.956 19.4667 17.8846V14.1155C19.4667 14.0441 19.4286 13.978 19.3667 13.9423L16.1026 12.0578ZM16.0026 10C12.6889 10 10.0026 12.6863 10.0026 16C10.0026 19.3138 12.6889 22 16.0026 22C16.3708 22 16.6693 22.2985 16.6693 22.6667C16.6693 23.0349 16.3708 23.3334 16.0026 23.3334C11.9525 23.3334 8.66927 20.0501 8.66927 16C8.66927 11.95 11.9525 8.66671 16.0026 8.66671C20.0527 8.66671 23.3359 11.95 23.3359 16L24.1983 16C24.4056 16 24.5336 16.2262 24.4269 16.4039L22.8979 18.9523C22.7944 19.1249 22.5442 19.1249 22.4406 18.9523L20.9116 16.4039C20.8049 16.2262 20.933 16 21.1402 16L22.0026 16C22.0026 12.6863 19.3163 10 16.0026 10Z" fill="#10ABFF" />
                            </svg>
                            <div>
                                <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Automation</Text.Heading>
                                <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Everything from location based plugin reminders to scheduling your climate while on the go</Text.Body>
                            </div>
                        </FactsCol>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Text.Heading color={styles.colors.dimHighlight} type='h5' variant='bold' style={{ textAlign: 'center', marginBottom: '16px' }}>Explore the full set of features</Text.Heading>
                        <StyledRow>
                            <LinkButton variant='secondary' as='a' href='/subscribe#features'>See Plans &amp; Features</LinkButton>
                        </StyledRow>
                    </Col>
                </Row>
            </StyledContainer>
            <Text.Heading type='h6' variant='bold' color={styles.colors.lightGray} style={{ textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '13px', lineHeight: '19px', marginBottom: '12px' }}>
                The verdict is in
            </Text.Heading>
            <Text.Heading type='h3' tablet='h4' variant='bold' style={{ textAlign: 'center', maxWidth: '560px', margin: '0 auto 40px', padding: '0 24px' }}>
                TezLab makes the EV experience incredible.
            </Text.Heading>
            <FunkyImagesContainer>
                <FunkyImage src={zoomy_tesla} />
                <FunkyImage src={facts_tile} isFacts isLarge>
                    {hasOdometer && (
                        <LiveEyebrow>
                            <LiveDot />
                            <span><b>Live</b></span>
                        </LiveEyebrow>
                    )}
                    {hasOdometer && (
                        <div>
                            <LiveMilesValue odometer={fleet_odometer} />
                            <Text.Body color={styles.colors.lightGray} style={{ marginTop: '8px' }}>{fleet_odometer.units === 'km' ? 'Kilometers' : 'Miles'} driven by TezLab members</Text.Body>
                        </div>
                    )}
                    <StatGrid>
                        <div>
                            <StatValue>99%</StatValue>
                            <InfoCaption color={styles.colors.mediumGray} style={{ marginTop: '4px' }}>Supercharger Locations Visited</InfoCaption>
                        </div>
                        <div>
                            <StatValue>79%</StatValue>
                            <InfoCaption color={styles.colors.mediumGray} style={{ marginTop: '4px' }}>Avg Efficiency</InfoCaption>
                        </div>
                    </StatGrid>
                </FunkyImage>
                <FunkyImage src={sweet_car_image} isLarge />
                <FunkyImage src={cybertruck} />
            </FunkyImagesContainer>
        </ScreenWrapper>
    )
}